import { drawingDimStyle } from './dimFormat';
import type {
  Bounds,
  Discipline,
  Drawing,
} from '../shared/types';
import { boundsSize } from './geometry';
import { render } from './render';
import type { PaintItem } from './layers';
import { Viewport } from './viewport';

export interface PrintImage {
  dataUrl: string;
  landscape: boolean;
  width: number;
  height: number;
}

/**
 * Render a world-space region of the drawing to a high-resolution PNG data URL,
 * suitable for previewing and printing. The schedule tables are included when
 * `showSchedule` is on.
 */
export function renderRegion(
  drawing: Drawing,
  bounds: Bounds,
  opts: {
    showGrid: boolean;
    showSchedule: boolean;
    /** The layered paint order (and whether Imported is hidden); see render.ts. */
    paint?: PaintItem[];
    hideImported?: boolean;
    showHatches?: boolean;
    /** The lights' beams in sections (the status bar's Beams). */
    showBeams?: boolean;
    /** Every room's ceiling lines and arrows (View ▸ Show ceilings). */
    showCeilings?: boolean;
    disciplineFilters?: Record<Discipline, boolean>;
    margin?: number;
    /** Decoded underlay images (keyed by data URL), so imported PDFs print too. */
    backgroundImgs?: Map<string, CanvasImageSource>;
  }
): PrintImage {
  const size = boundsSize(bounds);
  const aspect = size.x / size.y;
  const MAX = 2000; // longest edge in pixels
  let width: number;
  let height: number;
  if (aspect >= 1) {
    width = MAX;
    height = Math.max(1, Math.round(MAX / aspect));
  } else {
    height = MAX;
    width = Math.max(1, Math.round(MAX * aspect));
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  const vp = new Viewport();
  vp.fit(bounds, width, height, opts.margin ?? 0.04);

  render(ctx, vp, drawing, {
    showGrid: opts.showGrid,
    paint: opts.paint,
    hideImported: opts.hideImported,
    selectedId: null,
    selectedDimId: null,
    showSchedule: opts.showSchedule,
    showScheduleTables: opts.showSchedule, // model prints mirror the model view (lines + tables)
    showHatches: opts.showHatches,
    showBeams: opts.showBeams,
    showCeilings: opts.showCeilings,
    disciplineFilters: opts.disciplineFilters,
    lang: drawing.language ?? 'it',
    dimStyle: drawingDimStyle(drawing),
    lightTheme: true,
    // Include the user-drawn vectors and the imported PDF underlay in prints.
    polygons: drawing.polygons ?? [],
    walls: drawing.walls ?? [],
    texts: drawing.texts ?? [],
    backgroundImages: (drawing.backgroundImages ?? []).flatMap((bi) => {
      const img = opts.backgroundImgs?.get(bi.dataUrl);
      return img ? [{ img, ...bi }] : [];
    }),
  });

  return { dataUrl: canvas.toDataURL('image/png'), landscape: aspect >= 1, width, height };
}

/**
 * Render a fixed square thumbnail (default 256x256) of a region, returning the
 * smaller of a PNG and a JPEG(q85) data URL. Line-art drawings compress smaller
 * as PNG; previews dominated by a raster/PDF underlay compress smaller as JPEG,
 * so picking the shorter encoding per file wins in both cases. A white backdrop
 * is painted first so the alpha-less JPEG matches the light theme.
 */
export function renderPreview(
  drawing: Drawing,
  bounds: Bounds,
  opts: {
    showHatches?: boolean;
    showBeams?: boolean;
    showCeilings?: boolean;
    disciplineFilters?: Record<Discipline, boolean>;
    backgroundImgs?: Map<string, CanvasImageSource>;
    size?: number;
    paint?: PaintItem[];
    hideImported?: boolean;
  } = {}
): string {
  const size = opts.size ?? 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);
  const vp = new Viewport();
  vp.fit(bounds, size, size, 0.06);
  render(ctx, vp, drawing, {
    showGrid: false,
    paint: opts.paint,
    hideImported: opts.hideImported,
    selectedId: null,
    selectedDimId: null,
    showSchedule: false,
    showScheduleTables: false,
    showLabels: false, // previews omit product-name labels
    showHatches: opts.showHatches,
    showBeams: opts.showBeams,
    showCeilings: opts.showCeilings,
    disciplineFilters: opts.disciplineFilters,
    lang: drawing.language ?? 'it',
    dimStyle: drawingDimStyle(drawing),
    lightTheme: true,
    polygons: drawing.polygons ?? [],
    walls: drawing.walls ?? [],
    texts: [], // previews omit text comments (and schedules stay off above)
    backgroundImages: (drawing.backgroundImages ?? []).flatMap((bi) => {
      const img = opts.backgroundImgs?.get(bi.dataUrl);
      return img ? [{ img, ...bi }] : [];
    }),
  });
  const png = canvas.toDataURL('image/png');
  const jpg = canvas.toDataURL('image/jpeg', 0.85);
  return jpg.length < png.length ? jpg : png;
}
